import React, { useContext, useEffect, useState, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import axios from "axios";
import AuthContext from "../../Context/AuthContext";
import authHeader from "../../utils/authHeader";
import MobilitySummary from "../results/MobilitySummary";
import DataLoader from "../results/DataLoader";
import MobilityMap from "../results/map";
import ResultsPanel from "../results/ResultPanel";
import { aggregateMobilityRows, matchMobilityByAddress, normalizeMobilityRows } from "../../utils/mobility";
import { fmtDate, tl } from "../../utils/format";
import { normalizeCoordinateNumber, normalizeCoordinateString } from "../../utils/coordinates";
import { normalizeUnitType } from "../../utils/unitTypes";

const API_BASE = "https://opt-api.vercel.app";
const CITY_MAP_CENTERS = {
    "İstanbul": { lat: 41.0082, lng: 28.9784 },
    Istanbul: { lat: 41.0082, lng: 28.9784 },
    Ankara: { lat: 39.9334, lng: 32.8597 },
    "İzmir": { lat: 38.4237, lng: 27.1428 },
    Izmir: { lat: 38.4237, lng: 27.1428 },
};
const number = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};
const usesFrequency = (line) => line?.planType === "Dijital" && line?.usageType === "Birim";
const usesUsageArea = (line) => line?.planType === "Poster" && line?.usageType === "Birim";
const usesUnitOnlyPricing = (line) => usesUsageArea(line) && ["Operasyon", "Baskı"].includes(line?.usageArea);
const lineBudget = (line) => {
    if (line?.totalBudget !== undefined) return number(line.totalBudget);
    if (line?.usageType === "Free") return 0;
    if (line?.usageType === "Paket") return number(line.packagePrice);
    if (usesUnitOnlyPricing(line)) return number(line?.unitPrice) * number(line?.unitNumber);
    const base = number(line?.unitPrice) * number(line?.period) * number(line?.unitNumber);
    return usesFrequency(line) ? base * number(line?.frequency) : base;
};
const HERO_LINE_COLUMNS = [
    { key: "dateRange", label: "Tarih", align: "left", minWidth: "min-w-[170px]" },
    { key: "period", label: "Periyot", align: "left", minWidth: "min-w-[80px]" },
    { key: "planUsage", label: "Plan / Kullanım", align: "left", minWidth: "min-w-[130px]" },
    { key: "usageArea", label: "Kullanım Alanı", align: "left", minWidth: "min-w-[120px]" },
    { key: "unitType", label: "Ünite Tipi", align: "left", minWidth: "min-w-[220px]" },
    { key: "city", label: "Şehir", align: "left", minWidth: "min-w-[110px]" },
    { key: "unitNumber", label: "Ünite Sayısı", align: "right", minWidth: "min-w-[110px]" },
    { key: "faces", label: "Face Sayısı", align: "right", minWidth: "min-w-[100px]" },
    { key: "frequency", label: "Frekans", align: "right", minWidth: "min-w-[90px]" },
    { key: "displayedPrice", label: "Ünite Fiyatı", align: "right", minWidth: "min-w-[120px]" },
    { key: "lineTotal", label: "Fiyat", align: "right", minWidth: "min-w-[120px]" },
];
const normalizeTableFilter = (value) =>
    String(value ?? "")
        .toLocaleLowerCase("tr-TR")
        .replaceAll("ç", "c")
        .replaceAll("ğ", "g")
        .replaceAll("ı", "i")
        .replaceAll("i̇", "i")
        .replaceAll("ö", "o")
        .replaceAll("ş", "s")
        .replaceAll("ü", "u");
const heroLineValue = (row, key) => {
    switch (key) {
        case "period":
        case "unitNumber":
        case "faces":
            return row[key]?.toLocaleString("tr-TR") || "";
        case "planUsage":
            return `${row.planType} / ${row.usageType}`;
        case "frequency":
            return row.frequency === null ? "—" : row.frequency?.toLocaleString("tr-TR") || "";
        case "displayedPrice":
            return tl(row.displayedPrice);
        case "lineTotal":
            return tl(row.lineTotal);
        default:
            return row[key] ?? "";
    }
};

export default function CampaignDetail() {
    const { id } = useParams();
    const history = useHistory();
    const { userData } = useContext(AuthContext);
    const isViewer = userData?.user?.user?.role === "viewer";

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [plan, setPlan] = useState(null);
    const [datasets, setDatasets] = useState([]);
    // Harita sadece desktop'ta gösterilir; mobilde hiç yüklenmez.
    const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 768px)").matches);

    useEffect(() => {
        const mediaQuery = window.matchMedia("(min-width: 768px)");
        const onChange = (event) => setIsDesktop(event.matches);
        mediaQuery.addEventListener("change", onChange);
        return () => mediaQuery.removeEventListener("change", onChange);
    }, []);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                setLoading(true);
                setError("");
                const { data } = await axios.get(`${API_BASE}/Plans/${id}`, authHeader());
                if (!alive) return;
                setPlan(data);
            } catch (e) {
                if (!alive) return;
                setError(e?.response?.data?.hata || e.message || "Plan alınamadı");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [id]);

    // 1) sadece mobility olan dataset'ler
    const mobilityDatasets = useMemo(
        () => (datasets || []).filter(d => d.type === "mobility"),
        [datasets]
    );

    const normalizedMobilityRows = useMemo(
        () => normalizeMobilityRows(mobilityDatasets),
        [mobilityDatasets]
    );

    const campaignLines = useMemo(
        () => Array.isArray(plan?.campaignLines) ? plan.campaignLines : [],
        [plan]
    );

    const planAddresses = useMemo(() => {
        if (campaignLines.length) {
            return campaignLines.flatMap((line) => (line.locations || []).map((location) => ({
                ...location,
                city: line.city,
                unit: normalizeUnitType(line.unitType),
                unitType: normalizeUnitType(location.unitType || line.unitType),
                planType: line.planType || plan?.planType,
                usageType: line.usageType,
                usageArea: line.usageArea,
                startDate: line.startDate,
                endDate: line.endDate,
                period: line.period,
                unitNumber: line.unitNumber,
                faces: line.faces,
                unitPrice: line.unitPrice,
                frequency: line.frequency,
                packagePrice: line.packagePrice,
            })));
        }
        return Array.isArray(plan?.addresses) ? plan.addresses : [];
    }, [campaignLines, plan]);

    const mobilityPlan = useMemo(
        () => plan ? { ...plan, addresses: planAddresses } : null,
        [plan, planAddresses]
    );

    // plan.addresses → DB'deki koordinatları kullan. Haritada yalnızca planın kendi
    // koordinatı olan lokasyonlar gösterilir; mobility Excel'inden "ödünç" koordinat
    // alıp adres metniyle eşleştirme fallback'i kaldırıldı (yanlış/hayalet eşleşmelere
    // yol açıyordu).
    const allPlanPoints = useMemo(() => {
        if (!planAddresses.length) return [];
        return planAddresses
            .map((a, i) => {
                const lat = normalizeCoordinateNumber(a.latitude, "lat");
                const lng = normalizeCoordinateNumber(a.longitude, "lng");
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
                const normalizedLatitude = normalizeCoordinateString(a.latitude, "lat");
                const normalizedLongitude = normalizeCoordinateString(a.longitude, "lng");
                const mobilityMatches = matchMobilityByAddress({
                    address: a.address,
                    latitude: normalizedLatitude,
                    longitude: normalizedLongitude,
                }, normalizedMobilityRows);
                return {
                    id: a._id || `plan-${i}`,
                    lat,
                    lng,
                    title: a.address,
                    unitType: normalizeUnitType(a.unitType || a.unit || ""),
                    mobility: mobilityMatches.length ? aggregateMobilityRows(mobilityMatches) : null,
                };
            })
            .filter(Boolean);
    }, [planAddresses, normalizedMobilityRows]);

    const mapPoints = allPlanPoints;

    const resultPlan = useMemo(() => {
        if (!plan) return null;
        const rows = campaignLines.length
            ? campaignLines.map((line) => ({
                city: line.city,
                unit: normalizeUnitType(line.unitType),
                startDate: line.startDate,
                endDate: line.endDate,
                period: line.period,
                unitNumber: line.unitNumber,
                faces: line.faces,
                unitPrice: line.unitPrice,
            }))
            : planAddresses.map((address) => ({
                city: address.city,
                unit: normalizeUnitType(address.unitType || address.unit),
                startDate: address.startDate,
                endDate: address.endDate,
                period: address.period,
                unitNumber: address.unitNumber,
                faces: address.faces,
                unitPrice: address.unitPrice,
            }));
        return { ...plan, rows };
    }, [campaignLines, plan, planAddresses]);

    const campaignStats = useMemo(
        () => buildCampaignStats(plan, campaignLines, planAddresses),
        [plan, campaignLines, planAddresses]
    );
    const mapInitialCenter = useMemo(() => {
        const cities = campaignStats.cities || [];
        return cities.includes("İstanbul") || cities.includes("Istanbul")
            ? CITY_MAP_CENTERS["İstanbul"]
            : CITY_MAP_CENTERS[cities[0]];
    }, [campaignStats.cities]);

    return (
        <div className="max-w-[1480px] mx-auto px-6 py-6 space-y-5">
            <div className="mb-4 flex items-center justify-between gap-3">
                <button onClick={() => history.push("/homepage")} className="h-9 px-3 border rounded-lg text-sm">
                    ← Geri Dön
                </button>
                {campaignLines.length > 0 && !isViewer && (
                    <button onClick={() => history.push(`/EditPlan/${id}`)} className="h-9 px-3 rounded-lg bg-brand-600 text-white text-sm hover:bg-brand-700">
                        Kampanyayı Düzenle
                    </button>
                )}
            </div>

            <div className="flex justify-end mb-3">
                <DataLoader onLoaded={(files) => setDatasets(prev => [...prev, ...files])} />
            </div>

            {loading && <div className="text-slate-600">Yükleniyor…</div>}
            {!loading && error && <div className="text-rose-600">{error}</div>}

            {!loading && !error && plan && (
                <>
                    <CampaignHero plan={plan} stats={campaignStats} />
                    {campaignLines.length > 0 && <CampaignLocationAccordions lines={campaignLines} />}
                    <ResultsPanel plan={resultPlan} datasets={(datasets || []).filter((dataset) => dataset.type !== "mobility")} />
                    {isDesktop && (
                        <MobilityMap
                            points={mapPoints}
                            initialCenter={mapInitialCenter}
                            title={campaignStats.mapTitle}
                            subtitle={campaignStats.mapSubtitle}
                            height="600px"
                        />
                    )}


                    <MobilitySummary plan={mobilityPlan} datasets={mobilityDatasets} />

                </>
            )}
        </div>
    );
}

function buildCampaignStats(plan, lines, addresses) {
    const safeLines = Array.isArray(lines) ? lines : [];
    const safeAddresses = Array.isArray(addresses) ? addresses : [];
    const sourceLines = safeLines.length
        ? safeLines
        : [{
            planType: plan?.planType,
            usageType: plan?.usageType,
            usageArea: plan?.usageArea,
            unitType: safeAddresses[0]?.unitType || safeAddresses[0]?.unit,
            city: safeAddresses[0]?.city,
            startDate: safeAddresses[0]?.startDate,
            endDate: safeAddresses[0]?.endDate,
            period: safeAddresses[0]?.period,
            unitNumber: safeAddresses.reduce((sum, item) => sum + number(item.quantity || item.unitNumber), 0),
            faces: safeAddresses.reduce((sum, item) => sum + number(item.faces || item.quantity), 0),
            unitPrice: safeAddresses[0]?.unitPrice,
        }];

    const unitTypes = Array.from(new Set(sourceLines.map((line) => normalizeUnitType(line.unitType)).filter(Boolean)));
    const planTypes = Array.from(new Set(sourceLines.map((line) => line.planType).filter(Boolean)));
    const cities = Array.from(new Set(sourceLines.map((line) => line.city).filter(Boolean)));
    const firstStart = sourceLines.map((line) => line.startDate).filter(Boolean).sort()[0];
    const lastEnd = sourceLines.map((line) => line.endDate).filter(Boolean).sort().slice(-1)[0];
    const periodTotal = sourceLines.reduce((sum, line) => sum + number(line.period), 0);
    const unitTotal = sourceLines.reduce((sum, line) => sum + number(line.unitNumber), 0);
    const faceTotal = sourceLines.reduce((sum, line) => sum + number(line.faces), 0);
    const budgetTotal = plan?.totalBudget !== undefined
        ? number(plan.totalBudget)
        : sourceLines.reduce((sum, line) => sum + lineBudget(line), 0);
    const firstPriceLine = sourceLines.find((line) =>
        number(line.usageType === "Paket" ? line.packagePrice : line.unitPrice) > 0
    );
    const displayedPrice = firstPriceLine
        ? number(firstPriceLine.usageType === "Paket" ? firstPriceLine.packagePrice : firstPriceLine.unitPrice)
        : 0;
    const locationCount = safeAddresses.length;
    const lineRows = sourceLines.map((line, index) => ({
        id: line._id || `${line.unitType || "line"}-${index}`,
        dateRange: line.startDate || line.endDate ? `${fmtDate(line.startDate)} – ${fmtDate(line.endDate)}` : "Tarih belirtilmedi",
        period: number(line.period),
        planType: line.planType || plan?.planType || "—",
        usageType: line.usageType || "—",
        usageArea: usesUsageArea(line) ? (line.usageArea || "Medya") : "—",
        unitType: normalizeUnitType(line.unitType) || "—",
        city: line.city || "—",
        unitNumber: number(line.unitNumber),
        faces: number(line.faces),
        frequency: usesFrequency(line) ? number(line.frequency) : null,
        locationCount: Array.isArray(line.locations)
            ? line.locations.length
            : safeAddresses.filter((address) => normalizeUnitType(address.unitType || address.unit) === normalizeUnitType(line.unitType)).length,
        displayedPrice: number(line.usageType === "Paket" ? line.packagePrice : line.unitPrice),
        lineTotal: lineBudget(line),
    }));

    return {
        unitTypes,
        planTypes,
        cities,
        dateRange: firstStart || lastEnd ? `${fmtDate(firstStart)} – ${fmtDate(lastEnd)}` : "Tarih belirtilmedi",
        periodText: `${periodTotal || number(sourceLines[0]?.period)} periyot`,
        unitTotal,
        faceTotal,
        locationCount,
        displayedPrice,
        budgetTotal,
        lineRows,
        mapTitle: `${unitTypes.join(", ") || "Lokasyon"} — ${cities.join(", ") || "Kampanya"} Lokasyonları`,
        mapSubtitle: unitTypes.length ? `${unitTypes.join(", ")} · ${locationCount}` : `${locationCount} lokasyon`,
    };
}

function CampaignHero({ plan, stats }) {
    const [showAllLines, setShowAllLines] = useState(false);
    const [lineFilters, setLineFilters] = useState({});
    const [showFilterBuilder, setShowFilterBuilder] = useState(false);
    const [filterDraft, setFilterDraft] = useState({ key: HERO_LINE_COLUMNS[0].key, value: "" });
    const filteredLineRows = useMemo(() => {
        const activeFilters = Object.entries(lineFilters)
            .map(([key, value]) => [key, normalizeTableFilter(value).trim()])
            .filter(([, value]) => value);
        if (!activeFilters.length) return stats.lineRows;
        return stats.lineRows.filter((row) =>
            activeFilters.every(([key, value]) => normalizeTableFilter(heroLineValue(row, key)).includes(value))
        );
    }, [lineFilters, stats.lineRows]);
    const hasMoreLines = filteredLineRows.length > 5;
    const visibleLineRows = showAllLines ? filteredLineRows : filteredLineRows.slice(0, 5);
    const activeFilterCount = Object.values(lineFilters).filter((value) => String(value || "").trim()).length;
    const updateLineFilter = (key, value) => {
        setShowAllLines(false);
        setLineFilters((current) => ({ ...current, [key]: value }));
    };
    const clearLineFilters = () => {
        setShowAllLines(false);
        setLineFilters({});
    };
    const selectedFilterColumn = HERO_LINE_COLUMNS.find((column) => column.key === filterDraft.key) || HERO_LINE_COLUMNS[0];
    const activeFilters = HERO_LINE_COLUMNS
        .map((column) => ({ ...column, value: lineFilters[column.key] }))
        .filter((column) => String(column.value || "").trim());
    const addLineFilter = () => {
        const value = String(filterDraft.value || "").trim();
        if (!value) return;
        updateLineFilter(filterDraft.key, value);
        setFilterDraft((current) => ({ ...current, value: "" }));
        setShowFilterBuilder(false);
    };
    const removeLineFilter = (key) => {
        setShowAllLines(false);
        setLineFilters((current) => {
            const next = { ...current };
            delete next[key];
            return next;
        });
    };

    return (
        <section className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                <div>
                    <h1 className="text-2xl font-extrabold tracking-tight text-slate-950">{plan.name}</h1>
                </div>
                <div className="text-left lg:text-right">
                    <div className="text-xs font-extrabold uppercase tracking-wide text-slate-400">Kampanya Toplam Bütçesi</div>
                    <div className="mt-1 text-3xl font-black text-slate-950">{tl(stats.budgetTotal)}</div>
                </div>
            </div>

            <div className="my-6 h-px bg-slate-100" />

            {activeFilterCount > 0 && (
                <div className="mb-3 flex items-center justify-between rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                    <span>
                        {filteredLineRows.length.toLocaleString("tr-TR")} / {stats.lineRows.length.toLocaleString("tr-TR")} satır gösteriliyor
                    </span>
                    <button type="button" onClick={clearLineFilters} className="font-bold hover:underline">
                        Filtreleri temizle
                    </button>
                </div>
            )}

            <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3">
                <div className="flex flex-wrap items-center justify-end gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setShowFilterBuilder((value) => !value)}
                            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-bold text-slate-700 hover:bg-slate-50"
                        >
                            + Filtre Ekle
                        </button>
                        {activeFilters.map((filter) => (
                            <span key={filter.key} className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1.5 text-xs font-bold text-blue-800">
                                {filter.label}: {filter.value}
                                <button type="button" onClick={() => removeLineFilter(filter.key)} className="text-blue-500 hover:text-blue-800" aria-label={`${filter.label} filtresini kaldır`}>
                                    ×
                                </button>
                            </span>
                        ))}
                    </div>
                    {activeFilterCount > 0 && (
                        <button type="button" onClick={clearLineFilters} className="text-xs font-bold text-blue-700 hover:underline">
                            Tüm filtreleri temizle
                        </button>
                    )}
                </div>

                {showFilterBuilder && (
                    <div className="mt-3 grid gap-3 rounded-lg border border-slate-200 bg-white p-2 md:grid-cols-[220px_minmax(220px,1fr)_auto]">
                        <select
                            value={filterDraft.key}
                            onChange={(event) => setFilterDraft({ key: event.target.value, value: "" })}
                            className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 outline-none focus:border-blue-400"
                        >
                            {HERO_LINE_COLUMNS.map((column) => (
                                <option key={column.key} value={column.key}>{column.label}</option>
                            ))}
                        </select>
                        <input
                            type="search"
                            value={filterDraft.value}
                            onChange={(event) => setFilterDraft((current) => ({ ...current, value: event.target.value }))}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") addLineFilter();
                            }}
                            placeholder={`${selectedFilterColumn.label} filtre değeri`}
                            className={`h-9 rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 outline-none placeholder:text-slate-300 focus:border-blue-400 ${selectedFilterColumn.align === "right" ? "text-right" : ""}`}
                        />
                        <button
                            type="button"
                            onClick={addLineFilter}
                            disabled={!String(filterDraft.value || "").trim()}
                            className="h-9 rounded-md bg-slate-900 px-4 text-sm font-bold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            Ekle
                        </button>
                    </div>
                )}
            </div>

            <div className="hidden">
            <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                    <div className="text-xs font-extrabold uppercase tracking-wide text-slate-500">Satır Filtreleri</div>
                    {activeFilterCount > 0 && (
                        <button type="button" onClick={clearLineFilters} className="text-xs font-bold text-blue-700 hover:underline">
                            Filtreleri temizle
                        </button>
                    )}
                </div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
                    {HERO_LINE_COLUMNS.map((column) => (
                        <label key={column.key} className="block">
                            <span className="mb-1 block text-[11px] font-extrabold uppercase tracking-wide text-slate-400">{column.label}</span>
                            <input
                                type="search"
                                value={lineFilters[column.key] || ""}
                                onChange={(event) => updateLineFilter(column.key, event.target.value)}
                                placeholder="Filtrele"
                                aria-label={`${column.label} filtresi`}
                                className={`h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-xs font-medium text-slate-700 outline-none placeholder:text-slate-300 focus:border-blue-400 ${column.align === "right" ? "text-right" : ""}`}
                            />
                        </label>
                    ))}
                </div>
            </div>

            </div>

            <div className="overflow-x-auto">
                <table className="min-w-[1280px] w-full text-sm">
                    <thead className="border-b-2 border-slate-700">
                        <tr className="text-left text-xs font-extrabold uppercase tracking-wide text-slate-400">
                            <th className="pb-3">Tarih</th>
                            <th className="pb-3">Periyot</th>
                            <th className="pb-3">Plan / Kullanım</th>
                            <th className="pb-3">Kullanım Alanı</th>
                            <th className="pb-3">Ünite Tipi</th>
                            <th className="pb-3">Şehir</th>
                            <th className="pb-3 text-right">Ünite Sayısı</th>
                            <th className="pb-3 text-right">Face Sayısı</th>
                            <th className="pb-3 text-right">Frekans</th>
                            {/* <th className="pb-3 text-right">Haritadaki Lokasyon</th> */}
                            <th className="pb-3 text-right">Ünite Fiyatı</th>
                            <th className="pb-3 text-right">Fiyat</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {visibleLineRows.map((row, index) => (
                            <tr key={row.id} className={`text-slate-900 ${index % 2 === 0 ? "bg-white" : "bg-slate-100/80"}`}>
                                <td className="py-3 font-bold">{row.dateRange}</td>
                                <td className="py-3 font-normal">{row.period.toLocaleString("tr-TR")}</td>
                                <td className="py-3 font-normal">{row.planType} / {row.usageType}</td>
                                <td className="py-3 font-normal">{row.usageArea}</td>
                                <td className="py-3 font-bold">{row.unitType}</td>
                                <td className="py-3 font-normal">{row.city}</td>
                                <td className="py-3 text-right font-normal tabular-nums">{row.unitNumber.toLocaleString("tr-TR")}</td>
                                <td className="py-3 text-right font-normal tabular-nums">{row.faces.toLocaleString("tr-TR")}</td>
                                <td className="py-3 text-right font-normal tabular-nums">{row.frequency === null ? "—" : row.frequency.toLocaleString("tr-TR")}</td>
                                {/* <td className="py-3 text-right font-black tabular-nums">{row.locationCount.toLocaleString("tr-TR")}</td> */}
                                <td className="py-3 text-right font-normal tabular-nums">{tl(row.displayedPrice)}</td>
                                <td className="py-3 text-right font-black tabular-nums">{tl(row.lineTotal)}</td>
                            </tr>
                        ))}
                        {!visibleLineRows.length && (
                            <tr>
                                <td colSpan={HERO_LINE_COLUMNS.length} className="py-6 text-center text-sm font-medium text-slate-400">
                                    Filtrelere uygun satır bulunamadı.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {hasMoreLines && (
                <div className="mt-4 flex justify-end">
                    <button
                        type="button"
                        onClick={() => setShowAllLines((value) => !value)}
                        className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
                    >
                        {showAllLines
                            ? "İlk 5 satırı göster"
                            : `Tüm satırları göster (${filteredLineRows.length})`}
                    </button>
                </div>
            )}
        </section>
    );
}

function CampaignLocationAccordions({ lines }) {
    const groups = useMemo(() => {
        const grouped = new Map();

        (lines || []).forEach((line, lineIndex) => {
            const unitType = normalizeUnitType(line?.unitType) || "Ünite";
            const city = line?.city || "Şehir";
            const key = `${unitType}__${city}`;

            if (!grouped.has(key)) {
                grouped.set(key, {
                    key,
                    unitType,
                    city,
                    locations: [],
                });
            }

            const target = grouped.get(key);
            (line?.locations || []).forEach((location, locationIndex) => {
                target.locations.push({
                    id: `${line?._id || line?.id || lineIndex}-${location?._id || location?.id || locationIndex}`,
                    address: location?.address || location?.adres || location?.name || "Adres belirtilmedi",
                    count: number(location?.count ?? location?.adet ?? location?.quantity ?? location?.faces ?? 1),
                });
            });
        });

        return Array.from(grouped.values()).filter((group) => group.locations.length > 0);
    }, [lines]);
    const [showAllGroups, setShowAllGroups] = useState(false);
    const hasMoreGroups = groups.length > 5;
    const visibleGroups = showAllGroups ? groups : groups.slice(0, 5);

    if (!groups.length) return null;

    return (
        <section className="space-y-3">
            {visibleGroups.map((group) => (
                <details key={group.key} className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <summary className="flex cursor-pointer list-none items-center gap-2 px-5 py-4 text-base font-bold text-slate-800 [&::-webkit-details-marker]:hidden">
                        <span className="text-sm transition-transform group-open:rotate-90">▶</span>
                        <span>{group.unitType} — {group.city} Lokasyonları ({group.locations.length.toLocaleString("tr-TR")})</span>
                    </summary>

                    <div className="max-h-[360px] overflow-auto border-t border-slate-100">
                        <table className="w-full min-w-[520px] text-sm">
                            <thead className="sticky top-0 bg-slate-50 text-left text-xs font-extrabold uppercase tracking-wide text-slate-500">
                                <tr>
                                    <th className="px-5 py-3">Adres</th>
                                    <th className="px-5 py-3 text-right">Adet</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {group.locations.map((location, index) => (
                                    <tr key={location.id} className={index % 2 === 0 ? "bg-white" : "bg-slate-50/70"}>
                                        <td className="px-5 py-3 text-slate-800">{location.address}</td>
                                        <td className="px-5 py-3 text-right tabular-nums text-slate-900">
                                            {location.count.toLocaleString("tr-TR")}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </details>
            ))}
            {hasMoreGroups && (
                <div className="flex justify-end">
                    <button
                        type="button"
                        onClick={() => setShowAllGroups((value) => !value)}
                        className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
                    >
                        {showAllGroups
                            ? "İlk 5 lokasyon grubunu göster"
                            : `Tüm lokasyon gruplarını göster (${groups.length})`}
                    </button>
                </div>
            )}
        </section>
    );
}
